import React from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import { Project, Claim } from "@/types";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface BreadcrumbNavProps {
  className?: string;
}

export default function BreadcrumbNav({ className }: BreadcrumbNavProps) {
  const [location] = useLocation();
  const authenticatedRequest = useAuthenticatedRequest();
  
  // Parse route to determine context
  const paths = location.split("/").filter(Boolean);
  const isProjectRoute = paths[0] === "projects" && paths.length >= 2;
  const isDirectClaimRoute = paths[0] === "claims" && paths.length >= 2;
  const isNestedClaimRoute = isProjectRoute && paths.length >= 4 && paths[2] === "claims";
  
  const projectId = isProjectRoute ? paths[1] : null;
  const directClaimId = isDirectClaimRoute ? paths[1] : null;
  const nestedClaimId = isNestedClaimRoute ? paths[3] : null;
  const claimId = directClaimId || nestedClaimId;
  
  // Determine project sub-route
  const projectSubRoute = isProjectRoute && paths.length >= 3 && paths[2] !== "claims" ? paths[2] : null;
  
  // Fetch project data when needed - API returns project directly, not wrapped
  const { data: projectData, isLoading: projectLoading } = useQuery<Project>({
    queryKey: [`/api/projects/${projectId}`],
    queryFn: async () => {
      const res = await authenticatedRequest(`/api/projects/${projectId}`);
      if (!res.ok) throw new Error("Failed to fetch project");
      return res.json();
    },
    enabled: !!projectId,
  });
  
  // Fetch claim data when needed  
  const { data: claimData, isLoading: claimLoading } = useQuery<{ claim: Claim }>({
    queryKey: [`/api/claims/${claimId}`],
    queryFn: async () => {
      const res = await authenticatedRequest(`/api/claims/${claimId}`);
      if (!res.ok) throw new Error("Failed to fetch claim");
      return res.json();
    },
    enabled: !!claimId,
  });
  
  // For direct claims, fetch the project data separately
  const claimProjectId = claimData?.claim?.projectId;
  const { data: claimProjectData, isLoading: claimProjectLoading } = useQuery<Project>({
    queryKey: [`/api/projects/${claimProjectId}`],
    queryFn: async () => {
      const res = await authenticatedRequest(`/api/projects/${claimProjectId}`);
      if (!res.ok) throw new Error("Failed to fetch claim project");
      return res.json();
    },
    enabled: !!claimProjectId && isDirectClaimRoute,
  });
  
  // Don't render breadcrumbs for top-level routes
  if (!isProjectRoute && !isDirectClaimRoute) {
    return null;
  }
  
  // Wait for data to load before rendering
  const isLoadingData = (projectId && projectLoading) || (claimId && claimLoading) || (claimProjectId && claimProjectLoading);
  
  if (isLoadingData) {
    return null;
  }
  
  // Build breadcrumb items
  const breadcrumbItems = [];
  
  // Always start with Portfolio for project and claim routes
  breadcrumbItems.push({
    label: "Portfolio",
    href: "/projects",
    isCurrentPage: false,
  });
  
  // Get the effective project data (either direct project or claim's project)
  const effectiveProjectData = projectData || claimProjectData;
  const effectiveProjectId = projectId || claimProjectId;
  
  // Add project breadcrumb if we have project context
  if (effectiveProjectData && effectiveProjectId) {
    breadcrumbItems.push({
      label: effectiveProjectData.projectName,
      href: `/projects/${effectiveProjectId}`,
      isCurrentPage: !projectSubRoute && !claimId && isProjectRoute,
    });
    
    // Add project sub-route breadcrumb
    if (projectSubRoute && isProjectRoute) {
      const subRouteLabels: Record<string, string> = {
        "documents": "Documents",
        "team": "Team", 
        "edit": "Edit Project",
        "new-claim": "New Claim",
      };
      
      const subRouteLabel = subRouteLabels[projectSubRoute] || projectSubRoute;
      breadcrumbItems.push({
        label: subRouteLabel,
        href: `/projects/${effectiveProjectId}/${projectSubRoute}`,
        isCurrentPage: true,
      });
    }
  }
  
  // Add claim breadcrumb
  if (claimData?.claim) {
    breadcrumbItems.push({
      label: `Claim ${claimData.claim.claimIdentifier}`,
      href: isDirectClaimRoute ? `/claims/${claimId}` : `/projects/${effectiveProjectId}/claims/${claimId}`,
      isCurrentPage: true,
    });
  }
  
  if (breadcrumbItems.length <= 1) {
    return null;
  }
  
  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        {breadcrumbItems.map((item, index) => (
          <React.Fragment key={item.href}>
            <BreadcrumbItem>
              {item.isCurrentPage ? (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link href={item.href}>{item.label}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
} 